import { useMemo } from 'react';
import { usePollLiveData } from './queries/usePollLiveData';
import { useFetchPlaybackData } from './queries/useFetchPlaybackData';
import { usePrefetchPlaybackData } from './queries/usePrefetchPlaybackData';
import { type Timestamp } from '../services/Timestamp';
import { type MapData, Mode, type VesselPosition, type VesselViewModel } from '../types';

export const useMapData = (operationKey: string, mode: Mode, timestamp: Timestamp): MapData => {
  const { data: liveData, isLoading: isLiveDataLoading } = usePollLiveData(operationKey, mode);
  const queryResult = useFetchPlaybackData(timestamp, operationKey, mode);
  usePrefetchPlaybackData(timestamp, operationKey, queryResult, mode);
  const { data: playbackModel, isError, isLoading: isPlaybackDataLoading } = queryResult;

  const vesselData = useMemo((): Record<string, VesselViewModel> => {
    return (mode === Mode.Liveview ? liveData?.vesselData : playbackModel?.vesselData) ?? {};
  }, [mode, playbackModel, liveData]);

  const vesselPositions = useMemo((): VesselPosition[] => {
    if (mode === Mode.Liveview) {
      return liveData?.vesselPositions;
    }

    const playbackPositions = playbackModel?.vesselPositions;
    return playbackPositions && timestamp ? playbackPositions[timestamp.toSeconds()] : null;
  }, [mode, playbackModel?.vesselPositions, liveData, timestamp]);

  return { liveData, vesselPositions, vesselData, isPlaybackDataLoading, isError, isLiveDataLoading };
};
